// Task #6076 — single display resolver for a trip's cancellation policy label
// on admin surfaces. The policy truth is the stored rate plan (`rate_plan`,
// frozen at booking time, or the `refund_policy_snapshot` copy); the legacy
// `trips.cancellation_policy` column is usually null and only kept as a last
// textual fallback. Imported / migrated bookings (legacy CSV, bulk-migrated
// DriveShare, native-historic DriveShare) were settled off-platform and are
// non-refundable by convention — the same rule the renter-facing surfaces use
// (see imported-bookings-non-refundable) — so they never render blank.
// Display-only: never use this to gate a Stripe/refund action.

import { isImportedTrip } from "@/lib/legacy-import";

export interface CancellationPolicyTripShape {
  rate_plan?: string | null;
  refund_policy_snapshot?: { rate_plan?: string | null } | null;
  cancellation_policy?: string | null;
  source?: string | null;
  source_id?: number | string | null;
  platform_origin?: string | null;
}

export const POLICY_NOT_RECORDED = "Not recorded";

function labelForRatePlan(ratePlan: string | null | undefined): string | null {
  const rp = typeof ratePlan === "string" ? ratePlan.trim().toLowerCase() : "";
  if (rp === "flexible") return "Flexible";
  if (rp === "non_refundable" || rp === "non-refundable") return "Non-refundable";
  return null;
}

/**
 * Resolve the human-readable cancellation policy label for a trip.
 * Fallback chain: rate_plan → refund_policy_snapshot.rate_plan →
 * imported convention (Non-refundable) → raw cancellation_policy text →
 * explicit "Not recorded".
 */
export function resolveCancellationPolicyLabel(
  trip: CancellationPolicyTripShape | null | undefined,
): string {
  if (!trip) return POLICY_NOT_RECORDED;
  const fromRatePlan =
    labelForRatePlan(trip.rate_plan) ??
    labelForRatePlan(trip.refund_policy_snapshot?.rate_plan);
  if (fromRatePlan) return fromRatePlan;
  if (isImportedTrip(trip)) return "Non-refundable (imported booking)";
  const raw =
    typeof trip.cancellation_policy === "string" ? trip.cancellation_policy.trim() : "";
  if (raw !== "") {
    // Humanize a legacy free-text/slug value ("moderate", "strict_policy" …)
    const cleaned = raw.replace(/[_-]+/g, " ");
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }
  return POLICY_NOT_RECORDED;
}
